import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";

import Selider from "./component/Selider";
import Testimonial from "./component/Testimonial";
import Functionality from "./component/Functionality";
import Advanteg from "./component/Advanteg";
import IcbMobile from "./component/IcbMobile";
import Documentation from "./component/Documentation";
import Footer from "./component/Footer";
import i18n from "./i18n";
import LocaleContext from "./LocaleContext";
import {
  localeFromPathname,
  normalizePathLocale,
  replaceLocalePath,
} from "./localePath";
import { easeOut, transitionSoft } from "./animations";

const LANG_MENU = [
  { code: "ru", label: "RU" },
  { code: "en", label: "EN" },
  { code: "tj", label: "TJ" },
];

function selectLocaleValue(lng) {
  return normalizePathLocale(lng);
}

export default function App() {
  const [locale, setLocale] = useState(() => selectLocaleValue(i18n.language));

  useEffect(() => {
    const onLang = () => setLocale(selectLocaleValue(i18n.language));
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, []);

  useEffect(() => {
    const seg = localeFromPathname(window.location.pathname);
    if (seg) {
      if (selectLocaleValue(i18n.language) !== seg) {
        i18n.changeLanguage(seg);
      }
      return;
    }
    const code = selectLocaleValue(i18n.language);
    replaceLocalePath(code);
  }, []);

  useEffect(() => {
    const onPop = () => {
      const next = localeFromPathname(window.location.pathname);
      if (next) i18n.changeLanguage(next);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    if (!langOpen) return;
    const onDoc = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setLangOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [langOpen]);

  const pickLang = (code) => {
    i18n.changeLanguage(code);
    replaceLocalePath(code);
    setLangOpen(false);
  };

  const currentLang =
    LANG_MENU.find((item) => item.code === locale) || LANG_MENU[0];

  return (
    <div className="w-full bg-white">
      <LocaleContext.Provider value={{ locale, setLocale }}>
        <div className="sticky top-0 z-50 w-full bg-white/70 backdrop-blur-md supports-[backdrop-filter]:bg-white/60">
          <motion.div
            id="header"
            className="m-auto flex place-items-center justify-between border-b border-black/5 px-4 py-6 lg:max-w-[1024px] xl:max-w-[1280px]"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...transitionSoft, duration: 0.55 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...transitionSoft, delay: 0.08 }}
              className="flex items-center w-[100px]"
            >
              <img src="./img/ICB-Bank.svg" alt="" />
            </motion.div>

            <div ref={langRef} className="relative flex items-center gap-2">
              <motion.button
                type="button"
                id="lang"
                onClick={() => setLangOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                aria-label="Language"
                whileTap={{ scale: 0.97 }}
                className="flex min-w-[6.5rem] items-center justify-between gap-2 rounded-lg border border-[#E0E0E0] bg-white/70 px-3 py-2 text-sm font-semibold tracking-wider text-[#3D3D3D] shadow-sm outline-none transition hover:border-[#C8C8C8] focus-visible:border-[#949494] focus-visible:ring-2 focus-visible:ring-[#949494]/20"
              >
                <span className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 shrink-0 text-[#5E5E5E]"
                    aria-hidden
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                    <path d="M2 12h20" />
                  </svg>
                  {currentLang.label}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`h-3 w-3 shrink-0 transition-transform duration-200 ease-out ${
                    langOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </motion.button>

              <AnimatePresence>
                {langOpen ? (
                  <motion.ul
                    role="listbox"
                    aria-labelledby="lang"
                    initial={{ opacity: 0, y: -10, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.22, ease: easeOut }}
                    className="absolute right-0 top-full z-50 mt-2 min-w-[5.25rem] origin-top overflow-hidden rounded-lg border border-[#E0E0E0] bg-white/95 shadow-md backdrop-blur supports-[backdrop-filter]:bg-white/80"
                  >
                    {LANG_MENU.map(({ code, label }, i) => (
                      <motion.li
                        key={code}
                        role="presentation"
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: 0.04 + i * 0.04,
                          duration: 0.2,
                          ease: easeOut,
                        }}
                        className={i === 0 ? "" : "border-t border-[#EAEAEA]"}
                      >
                        <motion.button
                          type="button"
                          role="option"
                          tabIndex={0}
                          aria-selected={locale === code}
                          onClick={() => pickLang(code)}
                          whileHover={{
                            backgroundColor: "rgba(245,245,245,1)",
                          }}
                          className={`flex w-full items-center justify-center px-3 py-2 text-center text-sm font-semibold tracking-wider transition-colors ${
                            locale === code
                              ? "bg-[#EFEFEF] text-[#3D3D3D]"
                              : "text-[#5E5E5E]"
                          }`}
                        >
                          {label}
                        </motion.button>
                      </motion.li>
                    ))}
                  </motion.ul>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        <div className="w-full bg-[#F5F5F5]">
          <Selider />
        </div>
        <Testimonial />
        <Functionality />
        <Advanteg />
        <IcbMobile />
        <Documentation />
        <Footer />
      </LocaleContext.Provider>
    </div>
  );
}
